"use client";

import * as React from "react";
import { Slot as SlotPrimitive } from "radix-ui";

import {
  DropdownMenu as BaseMenu,
  DropdownMenuContent as BaseMenuContent,
  DropdownMenuItem as BaseMenuItem,
  DropdownMenuLabel as BaseMenuLabel,
  DropdownMenuSeparator as BaseMenuSeparator,
  DropdownMenuTrigger as BaseMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { RECORD_ACTION_ATTRIBUTE } from "@shared/assistant/page-snapshot";
import { useMobileMatchMedia } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

interface ActionMenuContextValue {
  phone: boolean;
  close: () => void;
}

const ActionMenuContext = React.createContext<ActionMenuContextValue>({
  phone: false,
  close: () => {},
});

function DropdownMenu({
  open,
  onOpenChange,
  children,
  ...props
}: React.ComponentProps<typeof BaseMenu>) {
  const phone = useMobileMatchMedia() === true;
  const [innerOpen, setInnerOpen] = React.useState(false);
  const isOpen = open ?? innerOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      setInnerOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  const context = React.useMemo(
    () => ({ phone, close: () => setOpen(false) }),
    [phone, setOpen],
  );
  if (!phone) {
    return (
      <ActionMenuContext.Provider value={context}>
        <BaseMenu open={open} onOpenChange={onOpenChange} {...props}>
          {children}
        </BaseMenu>
      </ActionMenuContext.Provider>
    );
  }
  return (
    <ActionMenuContext.Provider value={context}>
      <div className="contents" onClick={(event) => event.stopPropagation()}>
        <Sheet open={isOpen} onOpenChange={setOpen}>
          {children}
        </Sheet>
      </div>
    </ActionMenuContext.Provider>
  );
}

function DropdownMenuTrigger(
  props: React.ComponentProps<typeof BaseMenuTrigger>,
) {
  const { phone } = React.useContext(ActionMenuContext);
  if (!phone) return <BaseMenuTrigger {...props} />;
  const { asChild, children } = props;
  return <SheetTrigger asChild={asChild}>{children}</SheetTrigger>;
}

function DropdownMenuContent(
  props: React.ComponentProps<typeof BaseMenuContent>,
) {
  const { phone } = React.useContext(ActionMenuContext);
  if (!phone) return <BaseMenuContent {...props} />;
  return (
    <SheetContent
      side="bottom"
      layout="grouped"
      showCloseButton={false}
      aria-describedby={undefined}
      onCloseAutoFocus={props.onCloseAutoFocus}
    >
      <SheetTitle className="sr-only">Actions</SheetTitle>
      <SheetBody>
        <div className="mb-2 flex flex-col divide-y divide-border-subtle overflow-hidden rounded-xl bg-surface">{props.children}</div>
      </SheetBody>
    </SheetContent>
  );
}

const PHONE_ROW_CLASS =
  "flex min-h-11 w-full items-center gap-2 bg-surface px-4 py-2.5 text-left text-base outline-none active:bg-surface-3 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:text-muted-foreground";

interface PhoneMenuItemProps {
  variant?: "default" | "destructive";
  className?: string;
  asChild?: boolean;
  disabled?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  onSelect?: (event: Event) => void;
  children: React.ReactNode;
  "aria-label"?: string;
  "aria-describedby"?: string;
  title?: string;
  "data-record-action"?: string;
}

function PhoneMenuItem({
  variant = "default",
  className,
  asChild,
  disabled,
  onClick,
  onSelect,
  children,
  ...marks
}: PhoneMenuItemProps) {
  const { close } = React.useContext(ActionMenuContext);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    onClick?.(event);
    onSelect?.(event.nativeEvent);
    close();
  };
  const rowClass = cn(
    PHONE_ROW_CLASS,
    variant === "destructive" && "text-destructive-strong [&_svg]:text-destructive-strong",
    className,
  );
  if (asChild) {
    return (
      <SlotPrimitive.Slot
        {...marks}
        className={cn(rowClass, disabled && "pointer-events-none opacity-50")}
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : handleClick}
      >
        {children}
      </SlotPrimitive.Slot>
    );
  }
  return (
    <button type="button" {...marks} className={rowClass} disabled={disabled} onClick={handleClick}>
      {children}
    </button>
  );
}

function recordActionOf(props: object): string | undefined {
  const marker: unknown = Reflect.get(props, RECORD_ACTION_ATTRIBUTE);
  return typeof marker === "string" ? marker : undefined;
}

function DropdownMenuItem(props: React.ComponentProps<typeof BaseMenuItem>) {
  const { phone } = React.useContext(ActionMenuContext);
  if (!phone) return <BaseMenuItem {...props} />;
  const { variant, className, asChild, disabled, onClick, onSelect, children } = props;
  return (
    <PhoneMenuItem
      aria-label={props["aria-label"]}
      aria-describedby={props["aria-describedby"]}
      title={props.title}
      data-record-action={recordActionOf(props)}
      variant={variant ?? undefined}
      className={className}
      asChild={asChild}
      disabled={disabled}
      onClick={onClick}
      onSelect={onSelect}
    >
      {children}
    </PhoneMenuItem>
  );
}

function DropdownMenuLabel(
  props: React.ComponentProps<typeof BaseMenuLabel>,
) {
  const { phone } = React.useContext(ActionMenuContext);
  if (!phone) return <BaseMenuLabel {...props} />;
  return (
    <h2 className="bg-surface px-4 pt-3 pb-1 text-xs font-medium text-muted-foreground">
      {props.children}
    </h2>
  );
}

function DropdownMenuSeparator(
  props: React.ComponentProps<typeof BaseMenuSeparator>,
) {
  const { phone } = React.useContext(ActionMenuContext);
  if (!phone) return <BaseMenuSeparator {...props} />;
  return <div aria-hidden="true" className="h-2 bg-surface-2" />;
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
};
